// @ts-check
// Catálogo de ações do editor de fluxos (spec 2.0.0, fluxos.md seção 20, tabela de cores): categorias na ordem das
// sanfonas, cor do círculo e do ícone por categoria (tokens do tema, claro e escuro) e um item por ação. "Alterar
// responsável" e "Distribuir entre responsáveis" são o mesmo tipo de nó (ASSIGN_OWNER) com estratégias diferentes.
// Os nomes ficam em `flows.nodes.<TIPO>.name` (e `.nameRoundRobin`) e as categorias em `flows.catalog.categories.*`.

/** @typedef {'COMMUNICATION' | 'WAIT' | 'PATH' | 'LEAD' | 'OWNER' | 'INTEGRATIONS'} FlowActionCategory */

export const FLOW_ACTION_CATEGORIES = Object.freeze(['COMMUNICATION', 'WAIT', 'PATH', 'LEAD', 'OWNER', 'INTEGRATIONS']);

/**
 * Cores do círculo de cada categoria (`--flow-category-color` e `--flow-category-icon-color`). O âmbar leva o ícone
 * escuro do próprio âmbar, para ter contraste nos dois temas.
 * @type {Readonly<Record<FlowActionCategory, Readonly<{ background: string, icon: string }>>>}
 */
export const FLOW_CATEGORY_COLORS = Object.freeze({
  COMMUNICATION: Object.freeze({ background: '--t-color-purple9', icon: '--t-color-on-accent' }),
  WAIT: Object.freeze({ background: '--t-color-orange9', icon: '--t-color-on-accent' }),
  PATH: Object.freeze({ background: '--t-color-green9', icon: '--t-color-on-accent' }),
  LEAD: Object.freeze({ background: '--t-color-pink9', icon: '--t-color-on-accent' }),
  OWNER: Object.freeze({ background: '--t-color-amber9', icon: '--t-color-amber12' }),
  // o azul-céu claro pede ícone escuro, como o âmbar (contraste no tema claro)
  INTEGRATIONS: Object.freeze({ background: '--t-color-sky9', icon: '--t-color-sky12' }),
});

/**
 * @typedef {{ key: string, type: string, category: FlowActionCategory, icon: string, nameKey: string,
 *   config?: Readonly<Record<string, unknown>>, isSplit: boolean }} FlowCatalogItem
 *   config: o que o item muda na configuração padrão do tipo (só a estratégia do responsável)
 */

/** @type {ReadonlyArray<Readonly<FlowCatalogItem>>} */
export const FLOW_CATALOG_ITEMS = Object.freeze([
  { key: 'SEND_EMAIL', type: 'SEND_EMAIL', category: 'COMMUNICATION', icon: 'IconMail', nameKey: 'flows.nodes.SEND_EMAIL.name', isSplit: false },
  { key: 'SEND_WHATSAPP', type: 'SEND_WHATSAPP', category: 'COMMUNICATION', icon: 'IconBrandWhatsapp', nameKey: 'flows.nodes.SEND_WHATSAPP.name', isSplit: false },
  { key: 'WAIT', type: 'WAIT', category: 'WAIT', icon: 'IconClock', nameKey: 'flows.nodes.WAIT.name', isSplit: false },
  { key: 'WAIT_WINDOW', type: 'WAIT_WINDOW', category: 'WAIT', icon: 'IconCalendarTime', nameKey: 'flows.nodes.WAIT_WINDOW.name', isSplit: false },
  { key: 'WAIT_UNTIL_DATE', type: 'WAIT_UNTIL_DATE', category: 'WAIT', icon: 'IconCalendarClock', nameKey: 'flows.nodes.WAIT_UNTIL_DATE.name', isSplit: true },
  { key: 'CONDITION', type: 'CONDITION', category: 'PATH', icon: 'IconHierarchy2', nameKey: 'flows.nodes.CONDITION.name', isSplit: true },
  { key: 'EMAIL_ENGAGEMENT', type: 'EMAIL_ENGAGEMENT', category: 'PATH', icon: 'IconMailForward', nameKey: 'flows.nodes.EMAIL_ENGAGEMENT.name', isSplit: true },
  { key: 'ADD_TO_FLOW', type: 'ADD_TO_FLOW', category: 'PATH', icon: 'IconRoute', nameKey: 'flows.nodes.ADD_TO_FLOW.name', isSplit: false },
  { key: 'REMOVE_FROM_FLOWS', type: 'REMOVE_FROM_FLOWS', category: 'PATH', icon: 'IconLogout', nameKey: 'flows.nodes.REMOVE_FROM_FLOWS.name', isSplit: false },
  { key: 'UPDATE_STAGE', type: 'UPDATE_STAGE', category: 'LEAD', icon: 'IconProgressCheck', nameKey: 'flows.nodes.UPDATE_STAGE.name', isSplit: false },
  { key: 'UPDATE_FIELD', type: 'UPDATE_FIELD', category: 'LEAD', icon: 'IconEdit', nameKey: 'flows.nodes.UPDATE_FIELD.name', isSplit: false },
  { key: 'ADD_TAGS', type: 'ADD_TAGS', category: 'LEAD', icon: 'IconTags', nameKey: 'flows.nodes.ADD_TAGS.name', isSplit: false },
  { key: 'REMOVE_TAGS', type: 'REMOVE_TAGS', category: 'LEAD', icon: 'IconTags', nameKey: 'flows.nodes.REMOVE_TAGS.name', isSplit: false },
  {
    key: 'ASSIGN_OWNER', type: 'ASSIGN_OWNER', category: 'OWNER', icon: 'IconUserCheck', nameKey: 'flows.nodes.ASSIGN_OWNER.name',
    config: Object.freeze({ strategy: 'SPECIFIC' }), isSplit: false,
  },
  {
    key: 'DISTRIBUTE_OWNERS', type: 'ASSIGN_OWNER', category: 'OWNER', icon: 'IconUsersGroup', nameKey: 'flows.nodes.ASSIGN_OWNER.nameRoundRobin',
    config: Object.freeze({ strategy: 'ROUND_ROBIN' }), isSplit: false,
  },
  { key: 'CREATE_TASK', type: 'CREATE_TASK', category: 'OWNER', icon: 'IconChecklist', nameKey: 'flows.nodes.CREATE_TASK.name', isSplit: false },
  { key: 'SEND_NOTIFICATION', type: 'SEND_NOTIFICATION', category: 'OWNER', icon: 'IconBell', nameKey: 'flows.nodes.SEND_NOTIFICATION.name', isSplit: false },
  { key: 'CALL_WEBHOOK', type: 'CALL_WEBHOOK', category: 'INTEGRATIONS', icon: 'IconWebhook', nameKey: 'flows.nodes.CALL_WEBHOOK.name', isSplit: false },
  { key: 'HTTP_REQUEST', type: 'HTTP_REQUEST', category: 'INTEGRATIONS', icon: 'IconApi', nameKey: 'flows.nodes.HTTP_REQUEST.name', isSplit: false },
].map((item) => Object.freeze(/** @type {FlowCatalogItem} */ (item))));

const ITEMS_BY_KEY = new Map(FLOW_CATALOG_ITEMS.map((item) => [item.key, item]));

/** @param {string} key */
export const flowCatalogItem = (key) => ITEMS_BY_KEY.get(key) ?? null;

/**
 * Item do catálogo que corresponde a um nó (o responsável em rodízio é "Distribuir entre responsáveis").
 * @param {{ type: string, config?: Record<string, any> | null }} node
 */
export function catalogItemForNode(node) {
  if (node.type === 'ASSIGN_OWNER' && node.config?.strategy === 'ROUND_ROBIN') return ITEMS_BY_KEY.get('DISTRIBUTE_OWNERS') ?? null;
  return ITEMS_BY_KEY.get(node.type) ?? null;
}

/** Categoria de um tipo de nó, ou nulo para tipo desconhecido. @param {string} type @returns {FlowActionCategory | null} */
export const flowNodeCategory = (type) => ITEMS_BY_KEY.get(type)?.category ?? null;

/**
 * Ícone de um nó (nome de client/src/ui/icon-nodes.js).
 * @param {string} type @param {Record<string, any> | null} [config]
 */
export const flowNodeIcon = (type, config = null) => catalogItemForNode({ type, config })?.icon ?? 'IconHelpCircle';

/** Itens de uma categoria, na ordem do catálogo. @param {FlowActionCategory} category */
export const flowCatalogItemsOf = (category) => FLOW_CATALOG_ITEMS.filter((item) => item.category === category);
